import { computeSummary } from '../../grouping';
import { applyCellFormat } from './apply-format';
import { COLORS, SUBTOTAL_FUNCTION_CODE } from './styles';
import type { ExcelWriteContext } from './types';

/**
 * Emit a subtotal row — either for a single group or the grand total.
 * For each column that has a `summary` item, we write a live Excel
 * SUBTOTAL formula over the data-row range of the group. SUBTOTAL(9, …)
 * ignores nested SUBTOTAL cells inside its range, which means the same
 * formula works for outer groups even when inner subtotals sit inside
 * the range — Excel deduplicates them automatically.
 *
 * For columns with an `aggregate: 'custom'` summary item, we fall back
 * to the pre-computed scalar value (no SUBTOTAL equivalent exists for
 * arbitrary ratios), accepting that filter-time recalculation is lost.
 */
export function writeSubtotalRow<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet: any,
  firstDataRow: number,
  lastDataRow: number,
  rows: T[],
  label: string,
  ctx: ExcelWriteContext<T>,
  isGrand: boolean,
  outlineLevel = 0
): number {
  const cells: Record<string, unknown> = {};

  ctx.columns.forEach((col, idx) => {
    const summaryItem = ctx.summary?.find((s) => s.columnId === col.id);

    // Anchor label on the first column when no summary targets it — gives
    // every subtotal / grand total row a visible "Total" / "Sous-total X"
    // marker even when the leftmost column is purely descriptive (name, SKU).
    if (idx === 0 && !summaryItem) {
      cells[col.id] = label;
      return;
    }
    if (!summaryItem) {
      cells[col.id] = null;
      return;
    }

    const aggregate = summaryItem.aggregate ?? 'sum';
    const code = SUBTOTAL_FUNCTION_CODE[aggregate];
    if (code !== null && code !== undefined) {
      const letter = ctx.letterByColId.get(col.id) ?? 'A';
      // Cache the aggregated value alongside the SUBTOTAL formula so
      // older Excel versions (2013/2010) display the right number from
      // the start instead of 0 while still keeping the formula live for
      // filter-time recalculation.
      const cached = computeSummary(summaryItem, col, rows);
      cells[col.id] = {
        formula: `SUBTOTAL(${code},${letter}${firstDataRow}:${letter}${lastDataRow})`,
        result: cached ?? 0,
      };
    } else {
      const value = summaryItem.compute?.(rows) ?? null;
      cells[col.id] = value;
    }
  });

  const row = sheet.addRow(cells);

  // Apply per-column numFmt + alignment so SUBTOTAL formulas display
  // with the right currency/number format (otherwise they show as
  // generic floats ignoring the column's semantic type).
  for (const col of ctx.columns) {
    const cell = row.getCell(col.id);
    applyCellFormat(cell, col.id, ctx);
  }

  row.font = { bold: true };
  row.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: isGrand ? COLORS.grandTotalFill : COLORS.subtotalFill },
  };
  row.eachCell((c: { border: unknown }) => {
    c.border = {
      top: {
        style: isGrand ? 'medium' : 'thin',
        color: { argb: isGrand ? COLORS.grandTotalBorder : COLORS.subtotalBorder },
      },
    };
  });

  // Subtotals share the group header's outline level so they stay
  // visible when the user collapses the group — without this, the total
  // row would fold away with its own details and the summary would be
  // lost every time someone clicked the `−` button.
  if (outlineLevel > 0) {
    row.outlineLevel = outlineLevel;
  }

  return row.number;
}
